import React, { useEffect, useMemo, useRef, useState } from 'react';
import searchIcon from '../../../../../assets/icons/svg/shared/ic-shared-search.svg';
import { useTransactionsCategoriesQuery } from '../../../../../hooks/query/useTransactionsCategoriesQuery';
import { useStore } from '../../../../../store';
import Heading from '../../../../../generic-components/Heading';
import Button from '../../../../../generic-components/Button';
import { ICategory } from '../../../../../types';
import { LiliTransaction } from '../../../types/lili-transaction';
import { ReactComponent as DropDownIcon } from '../../../../../assets/svg/drop-down-category.svg';
import { TransactionCategorizationType } from '../../../enum/transaction-categorization-type';
import { ReactComponent as HappyFace } from '../../../../../assets/icons/svg/shared/ic-shared-happy-face.svg';

const ICONS_BASE_PATH = 'https://assets.lili.co/webapp/categories/';

interface IProps {
  isInlineMode?: boolean;
  transaction: LiliTransaction;
  onCategorySelect: (categoryId: string, businessPercent: number) => void;
  isDisabled?: boolean;
}

const DEFAULT_EXPENSE_CAT_ID = '55786EBF-C56E-4D3A-BF90-8CF1FF4CA933';

export const CategoryList = ({ isInlineMode, transaction, onCategorySelect, isDisabled }: IProps) => {
  const { customerId } = useStore((state) => state.currUser());
  const selectedTransaction = useStore((state) => state.selectedTransaction);
  const { data } = useTransactionsCategoriesQuery({ customerId, autoRefetch: true });
  const [isCategoriesPickerShown, setIsCategoriesPickerShown] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<ICategory | null>(null);
  const [filterQuery, setFilterQuery] = React.useState('');
  const elFloatList = useRef<HTMLUListElement>(null);
  const elInputFilter = useRef<HTMLInputElement>(null);
  const businessPercent = 1;
  const categories = data?.categories || [];

  const filterCategoryList = useMemo(() => {
    const typeFilterList = categories.filter(
      (category: ICategory) => category.transactionType === transaction.categorizationType,
    );
    if (isInlineMode) {
      return typeFilterList;
    }
    return typeFilterList.filter((category: ICategory) =>
      category.title.toLowerCase().includes(filterQuery.toLowerCase()));
  }, [categories, filterQuery, transaction]);

  useEffect(() => {
    if (!categories) return;
    if (transaction.categoryDetails?.categoryExternalId) {
      const preSelectedCategory = categories.filter(
        (c: ICategory) => c.id === transaction?.categoryDetails?.categoryExternalId,
      )[0];
      setSelectedCategory(preSelectedCategory);
    }
  }, [categories]);

  useEffect(() => {
    if (selectedCategory?.title && isInlineMode) {
      setFilterQuery(selectedCategory?.title);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (transaction.categoryDetails?.categoryExternalId) {
      const preSelectedCategory = categories.filter(
        (c: ICategory) => c.id === transaction?.categoryDetails?.categoryExternalId,
      )[0];
      setSelectedCategory(preSelectedCategory);
    } else {
      setSelectedCategory(null);
      setFilterQuery('');
    }
  }, [transaction]);

  const handleCategorySelect = (category: ICategory) => {
    setSelectedCategory(category);
    setIsCategoriesPickerShown(false);

    onCategorySelect(category.id, businessPercent);
  };
  return (
    <div className={`${isInlineMode ? '' : 'grow'} flex flex-col`}>
      <div className={`relative w-full   ${isInlineMode ? 'h-fit ' : 'grow'}`}>
        {/* Search category */}
        {!isInlineMode && (
          <div className=' relative z-[100]'>
            <div className='relative'>
              <img
                src={searchIcon}
                className='absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500'
                alt='search icon'
              />

              <input
                type='text'
                placeholder='Search category'
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                className='w-full rounded-[4px] border-[1px] border-bw04 p-[8px] pl-[36px] focus:outline-none font-metricR'
              />
            </div>
          </div>
        )}

        {isInlineMode && (
          <div className='relative w-full'>
            <input
              ref={elInputFilter}
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className={`py-[16px] placeholder:font-metricR ${isDisabled ? 'placeholder-red02' : 'placeholder-bw05'} font-metricRSemibold text-[16px] text-bw07 px-[16px] w-full rounded-[8px] border-bw06 border outline-none`}
              placeholder='Choose Category'
              onFocus={() => setIsCategoriesPickerShown(true)}
            />
            <Button
              className=''
              id=''
              onClick={() => elInputFilter?.current?.focus()}
              childElement={<DropDownIcon className='absolute top-1/2 -translate-y-1/2 right-0 translate-x-[-20px]' />}
            />
          </div>
        )}

        {/* Category list */}

        <ul
          ref={elFloatList}
          className={`absolute z-10 ${isInlineMode ? `${!isCategoriesPickerShown ? 'hidden' : ''} top-[60px] pt-[32px] bg-bw00 shadow-chatBotButton rounded-[8px] px-[12px] py-[12px] max-h-[400px] h-fit` : 'top-[60px] mt-[54px]'} left-0 inset-0 overflow-y-scroll`}
        >
          {filterCategoryList.map((category: any) => (
            <Button
              key={`button_${category.id}`}
              className='w-full'
              id={category.title?.replace(/[^a-z0-9s+]/gi, '')}
              onClick={() => handleCategorySelect(category)}
              childElement={
                <li
                  key={`li_${category.id}`}
                  className='flex justify-between px-[6px] py-[12px] border-b-[1px] border-bw03 hover:bg-action00 cursor-pointer'
                >
                  <div className='flex items-center'>
                    <img src={`${ICONS_BASE_PATH + category.imageName}.svg`} alt='icon' className='mr-[12px]' />
                    <p className='flex-grow font-metricR text-bw08 text-[16px]'>{category.title}</p>
                  </div>
                </li>
              }
            />
          ))}
          {!isInlineMode && selectedTransaction?.categorizationType === TransactionCategorizationType.EXPENSE && (
            <div className='h-[80px] pt-[8px]'>
              <Button
                className='w-full rounded-[12px] border border-bw08 py-[9px] hover:bg-bw02 shadow-topShadow'
                id='markAsPersonal'
                onClick={() => onCategorySelect(DEFAULT_EXPENSE_CAT_ID, 0)}
                childElement={
                  <div className='flex items-center justify-center w-full gap-[6px]'>
                    <HappyFace className='text-bw07' />
                    <Heading
                      title='Mark As Personal'
                      className='font-metricRMedium text-bw07 text-[16px] text-center'
                    />
                  </div>
                }
              />
            </div>
          )}
          {!filterCategoryList.length && (
            <Heading title='No categories found' className='font-metricR text-bw08 text-[16px]' />
          )}
        </ul>
      </div>
    </div>
  );
};
